import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import EdSpeedDial, {
	EdSpeedDialAction,
} from '@Components/UI/Utilities/EdSpeedDial/EdSpeedDial';
import { TableHeader } from '@Features/generics/generics.type';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { Chip, TableCell, Tooltip } from '@mui/material';
import {
	CardTargetChip,
	resolveProductLabel,
	resolveTargetType,
} from '@Pages/ScratchCards/components/CardTarget';
import { promoCodesGetAll } from '@Pages/PromoCodes/Slices/PromoCodes.slice';
import { FlexLayout } from '@Styled/utilities';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

const TABLE_HEADS: Array<TableHeader> = [
	{ displayName: 'CODE', fieldName: 'code', canSort: true },
	{ displayName: 'NAME', fieldName: 'name', canSort: true },
	{ displayName: 'TARGET', fieldName: 'target_type' },
	{ displayName: 'PRODUCT', fieldName: 'product_label' },
	{ displayName: 'DISCOUNT', fieldName: 'value' },
	{ displayName: 'USAGE', fieldName: 'current_uses' },
	{ displayName: 'MAX USES', fieldName: 'max_uses' },
	{ displayName: 'PER USER', fieldName: 'max_uses_per_user' },
	{ displayName: 'MIN AMOUNT', fieldName: 'min_amount_to_apply' },
	{ displayName: 'CAP', fieldName: 'value_cap' },
	{ displayName: 'EXPIRES', fieldName: 'expires_at', canSort: true },
	{ displayName: 'CREATED AT', fieldName: 'created_at', canSort: true },
];

const PromoCodesTable = () => {
	const dispatch = useAppThunkDispatch();
	const onPageRequestChange = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
			export: exportData,
		}: onPageChangeRequestMeta) => {
			dispatch(
				promoCodesGetAll({
					page: activePage,
					filters: filters,
					perPage: perPage ?? 10,
					query,
					export: exportData ?? false,
					sortBy: sortBy,
					from: startDate,
					to: endDate,
				})
			);
		},
		[dispatch]
	);

	const { fetchAll, filters, meta } = useTypedSelector(
		(state) => state.PromoCodes
	);

	const handleUpdate = (id?: number) => {
		navigate.push(`${navigate.location.pathname}/update/${id}`);
	};

	const navigate = useHistory();
	return (
		<div>
			<FlexLayout justifyContent="flex-end">
				<RoundButton
					edcolor="purple"
					startIcon={<EdIcon>add</EdIcon>}
					variant="contained"
					onClick={() => navigate.push(`${navigate.location.pathname}/create`)}
				>
					Create code
				</RoundButton>
			</FlexLayout>
			<MainTable
				tableFilters={filters}
				tableHeads={TABLE_HEADS}
				totalPages={meta?.last_page ?? 0}
				onPageChangeRequest={onPageRequestChange}
				RowsperPage={meta?.per_page}
				$hasActions
				pageNum={meta?.current_page}
				total={meta?.total}
				renderItems={() => {
					if (fetchAll.length === 0) {
						return (
							<MainTableRow>
								<TableCell colSpan={TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					} else {
						return fetchAll.map((code) => {
							const target = resolveTargetType(code);
							// Flat rows in the scope are admin-created wildcards on this
							// instructor; the portal shows them but never edits them
							// (the backend rejects update/delete on them anyway).
							const adminManaged = code.value_type !== 'percentage';
							return (
								<MainTableRow key={code.id}>
									<Tooltip title={code.code ?? ''} placement="top">
										<TableCell>{code.code}</TableCell>
									</Tooltip>
									<TableCell>{code.name ?? '-'}</TableCell>
									<TableCell>
										<CardTargetChip
											target={target}
											label={
												target === 'instructor' ? 'All my products' : undefined
											}
										/>
									</TableCell>
									<TableCell>{resolveProductLabel(code) ?? '-'}</TableCell>
									<TableCell>
										{code.value_type === 'percentage'
											? `${code.value}%`
											: `${code.value} (flat)`}
									</TableCell>
									<TableCell>{code.current_uses}</TableCell>
									<TableCell>{code.max_uses ?? '-'}</TableCell>
									<TableCell>{code.max_uses_per_user ?? '-'}</TableCell>
									<TableCell>{code.min_amount_to_apply ?? '-'}</TableCell>
									<TableCell>{code.value_cap ?? '-'}</TableCell>
									<TableCell>
										{code.expires_at ? (
											<DateTimeFormatter date={code.expires_at} />
										) : (
											'-'
										)}
									</TableCell>
									<TableCell>
										<DateTimeFormatter date={code.created_at} />
									</TableCell>
									<TableCell>
										{adminManaged ? (
											<Chip size="small" label="Admin-managed" />
										) : (
											<EdSpeedDial ariaLabel="Actions">
												<EdSpeedDialAction
													onClick={() =>
														navigate.push(
															`${navigate.location.pathname}/usage/${code.id}`
														)
													}
													key="usage"
													tooltipTitle="Usage"
													icon={<EdIcon>person</EdIcon>}
													aria-label=""
												/>
												<EdSpeedDialAction
													onClick={() => handleUpdate(code.id)}
													key="update"
													tooltipTitle="Update"
													icon={<EdIcon>edit</EdIcon>}
													aria-label=""
												/>
											</EdSpeedDial>
										)}
									</TableCell>
								</MainTableRow>
							);
						});
					}
				}}
			/>
		</div>
	);
};

export default PromoCodesTable;
